import { useState } from "react";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { Dialog } from "../../../../components/common/dialog";
import { Text } from "../../../../components/common/typography";
import { useBrands } from "../../hooks/useBrands";
import { useCategories } from "../../hooks/useCategories";
import { useProductWarranties } from "../../hooks/useProductWarranties";
import { useProductWarrantyTypes } from "../../hooks/useProductWarrantyTypes";
import { useProductWarrantyProviders } from "../../hooks/useProductWarrantyProviders";
import { useCountries } from "../../hooks/useCountries";
import { useProductDealTypes } from "../../hooks/useProductDealTypes";
import { useProductScreenSizes } from "../../hooks/useProductScreenSizes";
import { useProductResolutions } from "../../hooks/useProductResolutions";
import { useProductConditions } from "../../hooks/useProductConditions";
import { useProductCpus } from "../../hooks/useProductCpus";
import { useProductRams } from "../../hooks/useProductRams";
import { useProductStorageValues } from "../../hooks/useProductStorageValues";
import { useProductColors } from "../../hooks/useProductColors";

interface Props {
  open: boolean;
  onClose: () => void;
}

function ValueChips({ items }: { items: string[] }) {
  if (items.length === 0) return <span className="text-textMuted italic text-xs">None configured</span>;
  return (
    <div className="flex flex-wrap gap-1.5 pt-2">
      {items.map((v) => (
        <span key={v} className="rounded-md bg-bgMuted px-2 py-0.5 text-xs text-textBody">{v}</span>
      ))}
    </div>
  );
}

function FieldRow({ label, description, values }: { label: string; description: string; values?: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const hasValues = values && values.length > 0;

  return (
    <div className="py-1.5">
      <div className="flex items-center gap-2">
        <span className="font-medium text-textDark text-sm min-w-[150px] shrink-0">{label}</span>
        <span className="text-xs text-textMuted flex-1">{description}</span>
        {hasValues && (
          <button
            type="button"
            className="flex items-center gap-1 rounded-md border border-borderDefault px-2 py-0.5 text-xs text-textMuted hover:border-borderDark hover:text-textBody transition-colors shrink-0"
            onClick={() => setExpanded(!expanded)}
          >
            {values.length} values
            {expanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
          </button>
        )}
      </div>
      {expanded && hasValues && <ValueChips items={values} />}
    </div>
  );
}

export function BulkUploadGuideDialog({ open, onClose }: Props) {
  const { data: brands } = useBrands();
  const { data: categories } = useCategories();
  const { data: warranties } = useProductWarranties();
  const { data: warrantyTypes } = useProductWarrantyTypes();
  const { data: warrantyProviders } = useProductWarrantyProviders();
  const { data: countries } = useCountries();
  const { data: dealTypes } = useProductDealTypes();
  const { data: screenSizes } = useProductScreenSizes();
  const { data: resolutions } = useProductResolutions();
  const { data: conditions } = useProductConditions();
  const { data: cpus } = useProductCpus();
  const { data: rams } = useProductRams();
  const { data: storages } = useProductStorageValues();
  const { data: colors } = useProductColors();

  const names = (items: { name: string }[] | undefined) => (items ?? []).map((i) => i.name);

  return (
    <Dialog
      open={open}
      setOpen={onClose}
      title="Spreadsheet Column Guide"
      titleAlignment="left"
      titleSize="lg"
      showCloseButton
      showButtons={false}
      width="max-w-2xl"
      content={
        <div className="flex flex-col gap-3 text-sm text-textBody">
          <Text variant="bodySm" tone="muted">
            Each row = one variant. Rows with the same product name are grouped automatically. Click
            the value count to see available options for each field.
          </Text>

          <SectionHeader>Product columns</SectionHeader>
          <div className="flex flex-col divide-y divide-borderDefault rounded-xl border border-borderDefault px-4">
            <FieldRow label="Name" description="Product name (required)" />
            <FieldRow label="Brand" description="Product brand" values={names(brands)} />
            <FieldRow label="Category" description="Product category" values={names(categories)} />
            <FieldRow label="Description" description="Free-text description" />
            <FieldRow label="Warranty" description="Warranty period" values={names(warranties)} />
            <FieldRow label="WarrantyType" description="Service type" values={names(warrantyTypes)} />
            <FieldRow label="WarrantyProvider" description="Provider" values={names(warrantyProviders)} />
            <FieldRow label="CountryOfOrigin" description="Country" values={names(countries)} />
            <FieldRow label="ScreenSize" description="Display size" values={names(screenSizes)} />
            <FieldRow label="Resolution" description="Display resolution" values={names(resolutions)} />
            <FieldRow label="DealType" description="Deal category" values={names(dealTypes)} />
            <FieldRow label="MainImageURL" description="Product main image URL" />
            <FieldRow label="GalleryImageURLs" description="Extra images (semicolon-separated URLs)" />
            <FieldRow label="DatasheetURL" description="Link to datasheet" />
            <FieldRow label="Notes" description="Internal notes" />
          </div>

          <SectionHeader>Variant columns</SectionHeader>
          <div className="flex flex-col divide-y divide-borderDefault rounded-xl border border-borderDefault px-4">
            <FieldRow label="SKU" description="Unique variant code" />
            <FieldRow label="Storage" description="Storage spec" values={names(storages)} />
            <FieldRow label="RAM" description="Memory spec" values={names(rams)} />
            <FieldRow label="CPU" description="Processor" values={names(cpus)} />
            <FieldRow label="Color" description="Variant color" values={names(colors)} />
            <FieldRow label="Condition" description="Product condition" values={names(conditions)} />
            <FieldRow label="Model" description="Model name" />
            <FieldRow label="Quantity" description="Stock quantity" />
            <FieldRow label="ImageURL" description="Image URL" />
          </div>

          <SectionHeader>Pricing tiers</SectionHeader>
          <div className="rounded-xl border border-borderDefault px-4 py-3">
            <Text variant="bodySm" tone="body">
              Use <b>PricingTier_1_MinQty</b> + <b>PricingTier_1_Price</b> for the base tier (required).
              Add more tiers with <b>PricingTier_2_*</b>, <b>PricingTier_3_*</b>, etc. — no limit.
            </Text>
          </div>
        </div>
      }
    />
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <Text variant="bodyBold" tone="dark" className="mt-1">{children}</Text>;
}
